import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL, getAuthHeaders } from '../lib/utils';
import { useAuth } from './AuthContext';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [] });
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchCart();
    } else {
      // Load from localStorage for guest users
      const savedCart = localStorage.getItem('guestCart');
      if (savedCart) {
        setCart(JSON.parse(savedCart));
      }
    }
  }, [user]);

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${API_URL}/cart`, {
        headers: getAuthHeaders()
      });
      setCart(response.data);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    }
  };

  const addToCart = async (productOrItem, size, quantity = 1) => {
    // Handle both object and parameter-based calls
    const item = productOrItem.product_id ? productOrItem : {
      product_id: productOrItem.id,
      product_name: productOrItem.name,
      size,
      price: size === '125ml' ? productOrItem.price_125ml : productOrItem.price_250ml,
      quantity,
      image_url: productOrItem.image_url
    };

    if (user) {
      try {
        const response = await axios.post(`${API_URL}/cart`, item, {
          headers: getAuthHeaders()
        });
        setCart(response.data.cart);
      } catch (error) {
        console.error('Failed to add to cart:', error);
        throw error;
      }
    } else {
      // Guest cart
      const newCart = { ...cart };
      const existingIndex = newCart.items.findIndex(
        i => i.product_id === item.product_id && i.size === item.size
      );

      if (existingIndex >= 0) {
        newCart.items[existingIndex].quantity += quantity;
      } else {
        newCart.items.push(item);
      }

      setCart(newCart);
      localStorage.setItem('guestCart', JSON.stringify(newCart));
    }
  };

  const updateQuantity = async (productId, size, quantity) => {
    if (user) {
      try {
        const response = await axios.put(
          `${API_URL}/cart/${productId}?size=${size}&quantity=${quantity}`,
          {},
          { headers: getAuthHeaders() }
        );
        setCart(response.data.cart);
      } catch (error) {
        console.error('Failed to update cart:', error);
      }
    } else {
      const newCart = { ...cart };
      const item = newCart.items.find(i => i.product_id === productId && i.size === size);
      if (item) {
        if (quantity <= 0) {
          newCart.items = newCart.items.filter(i => !(i.product_id === productId && i.size === size));
        } else {
          item.quantity = quantity;
        }
        setCart(newCart);
        localStorage.setItem('guestCart', JSON.stringify(newCart));
      }
    }
  };

  const removeFromCart = async (productId, size) => {
    if (user) {
      try {
        const response = await axios.delete(
          `${API_URL}/cart/${productId}?size=${size}`,
          { headers: getAuthHeaders() }
        );
        setCart(response.data.cart);
      } catch (error) {
        console.error('Failed to remove from cart:', error);
      }
    } else {
      const newCart = { ...cart };
      newCart.items = newCart.items.filter(i => !(i.product_id === productId && i.size === size));
      setCart(newCart);
      localStorage.setItem('guestCart', JSON.stringify(newCart));
    }
  };

  const clearCart = async () => {
    if (user) {
      try {
        await axios.delete(`${API_URL}/cart`, { headers: getAuthHeaders() });
        setCart({ items: [] });
      } catch (error) {
        console.error('Failed to clear cart:', error);
      }
    } else {
      setCart({ items: [] });
      localStorage.removeItem('guestCart');
    }
  };

  const getCartTotal = () => {
    return cart.items.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartCount = () => {
    return cart.items.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <CartContext.Provider value={{
      cart,
      loading,
      addToCart,
      updateQuantity,
      removeFromCart,
      clearCart,
      getCartTotal,
      getCartCount
    }}>
      {children}
    </CartContext.Provider>
  );
};